import { Alert, Button } from 'antd'
import { observer } from 'mobx-react-lite'
import { useState } from 'react'
import { Link } from 'react-router'

import { Form, FormField } from '@/Core/UI/Form'
import { AppConfig } from '@/Ship/Configs/AppConfig'
import { classNames } from '@/Ship/Helpers/classNames'

import { PASSWORD_RECOVERY_TEXTS, PasswordRecoveryManager } from '../../Stores/PasswordRecoveryManager'

import styles from './ForgotPasswordForm.module.css'

const EMAIL_FIELDS = {
    email: { label: 'E-mail', isRequired: true, inputProps: { type: 'email', autoComplete: 'email' } },
}

const RESET_FIELDS = {
    code: {
        label: 'Код підтвердження',
        isRequired: true,
        inputProps: { maxLength: 6, inputMode: 'numeric', autoComplete: 'one-time-code' },
    },
    plainPassword: {
        label: 'Новий пароль',
        type: 'password',
        isRequired: true,
        inputProps: { autoComplete: 'new-password' },
    },
    confirmPassword: {
        label: 'Підтвердити пароль',
        type: 'password',
        isRequired: true,
        inputProps: { autoComplete: 'new-password' },
    },
}

const text = (value: unknown): string => (typeof value === 'string' ? value : String(value ?? ''))

/**
 * Відновлення пароля кодом на пошту, два кроки (`SPEC-005` §7.4).
 *
 * Власна форма, а не ядрова: у ядрової тексти кроків не змінити («Скинути
 * пароль» замість «Відновити пароль», немає повідомлення про надісланий
 * лист), а правити ядро звідси не можна. Запити й стан — у
 * `PasswordRecoveryManager`, тут лише розмітка.
 */
export const ForgotPasswordForm = observer(() => {
    const [manager] = useState(() => new PasswordRecoveryManager())

    const error = manager.error ? <Alert type="error" showIcon title={manager.error} /> : null

    if (manager.step === 'done') {
        return (
            <div className={classNames(styles['form'])}>
                <Alert type="success" showIcon title={PASSWORD_RECOVERY_TEXTS.done} />
                <Link to={AppConfig.authPageUrl}>
                    <Button type="primary" block>
                        Увійти
                    </Button>
                </Link>
            </div>
        )
    }

    if (manager.step === 'code') {
        return (
            <div className={classNames(styles['form'])}>
                <Alert type="success" showIcon title={PASSWORD_RECOVERY_TEXTS.codeSent} description={manager.email} />

                <Form
                    key="code"
                    fields={RESET_FIELDS}
                    autoComplete="off"
                    onSubmit={({ values }: { values: Record<string, unknown> }) =>
                        manager.resetPassword({
                            code: text(values['code']),
                            plainPassword: text(values['plainPassword']),
                            confirmPassword: text(values['confirmPassword']),
                        })
                    }
                >
                    <FormField name="code" />
                    <FormField name="plainPassword" />
                    <FormField name="confirmPassword" />

                    {error}

                    <div className={classNames(styles['buttons'])}>
                        <Button type="link" className={classNames(styles['linkButton'])} onClick={() => manager.back()}>
                            Назад
                        </Button>
                        <Button type="primary" htmlType="submit" loading={manager.isLoading}>
                            Встановити новий пароль
                        </Button>
                    </div>
                </Form>
            </div>
        )
    }

    return (
        <div className={classNames(styles['form'])}>
            <Form
                key="email"
                fields={EMAIL_FIELDS}
                onSubmit={({ values }: { values: Record<string, unknown> }) => manager.requestCode(text(values['email']))}
            >
                <FormField name="email" />

                {error}

                <div className={classNames(styles['buttons'])}>
                    <Link to={AppConfig.authPageUrl}>
                        <Button type="link" className={classNames(styles['linkButton'])}>
                            Увійти
                        </Button>
                    </Link>
                    <Button type="primary" htmlType="submit" loading={manager.isLoading}>
                        Відновити пароль
                    </Button>
                </div>
            </Form>
        </div>
    )
})

ForgotPasswordForm.displayName = 'ForgotPasswordForm'
